/**
 * Choose where an excerpt goes (#277): type-ahead over every destination type
 * - meetings, tasks, notes, scratchpads, projects, series, tags, Views and
 * people - multi-select, with recent destinations offered before typing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scissors, X, Check, NotebookPen } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { ENTITY_ICONS } from '../../lib/entityMeta';
import type { DestType, ItemRef } from '../../lib/excerpts';

export interface PickedItem extends ItemRef { title: string }

const DEST_TYPES = new Set<string>(['meeting_instance', 'task', 'note', 'scratchpad', 'scope', 'meeting_series', 'tag', 'reference_doc', 'person']);
const TYPE_LABEL: Record<string, string> = {
  meeting_instance: 'Meeting', task: 'Task', note: 'Note', scratchpad: 'Scratchpad', scope: 'Project',
  meeting_series: 'Series', tag: 'Tag', reference_doc: 'View', person: 'Person',
};
const RECENT_KEY = 'dossier:excerptRecentDestinations';
const MAX_RECENT = 6;

export function getRecentDestinations(): PickedItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((r) => r && DEST_TYPES.has(r.type) && typeof r.id === 'string' && typeof r.title === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberDestinations(items: PickedItem[]): void {
  try {
    const keyed = (i: PickedItem) => `${i.type}:${i.id}`;
    const fresh = new Set(items.map(keyed));
    const next = [...items, ...getRecentDestinations().filter((r) => !fresh.has(keyed(r)))].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private window): recents are a convenience only.
  }
}

interface SearchResult { entity_type: string; entity_id: string; name: string }

interface Props {
  title?: string;
  confirmLabel?: string;
  initial?: PickedItem[];
  /** The excerpt's own document: never offered. */
  exclude?: ItemRef;
  onConfirm: (picked: PickedItem[]) => void;
  onClose: () => void;
}

const keyOf = (i: { type: string; id: string }) => `${i.type}:${i.id}`;

export function DestinationPicker({ title = 'Send excerpt to', confirmLabel = 'Send', initial = [], exclude, onConfirm, onClose }: Props) {
  const [picked, setPicked] = useState<PickedItem[]>(initial);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedItem[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await apiGet<SearchResult[]>(`/entity-search?q=${encodeURIComponent(q)}&limit=20`);
        if (cancelled) return;
        setResults(data
          .filter((r) => DEST_TYPES.has(r.entity_type))
          .map((r) => ({ type: r.entity_type as DestType, id: r.entity_id, title: r.name })));
        setActive(0);
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const excludedKey = exclude ? keyOf(exclude) : '';
  const options = useMemo(() => {
    const list = query.trim() ? results : getRecentDestinations();
    return list.filter((o) => keyOf(o) !== excludedKey);
  }, [query, results, excludedKey]);

  const pickedKeys = new Set(picked.map(keyOf));
  const toggle = (item: PickedItem) => {
    setPicked((cur) => (cur.some((c) => keyOf(c) === keyOf(item)) ? cur.filter((c) => keyOf(c) !== keyOf(item)) : [...cur, item]));
  };
  const confirm = () => {
    rememberDestinations(picked);
    onConfirm(picked);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, options.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      if ((e.ctrlKey || e.metaKey) || options.length === 0) { if (picked.length || initial.length) confirm(); return; }
      const o = options[active];
      if (o) { toggle(o); setQuery(''); }
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/50 pt-[12vh] px-4" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-label={title}
        className="w-full max-w-md rounded-lg border border-border bg-bg-surface shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Scissors size={14} className="text-excerpt" />
          <span className="text-sm font-medium text-text-primary flex-1">{title}</span>
          <button type="button" aria-label="Close" onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
        </div>
        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1 px-3 pt-2">
            {picked.map((p) => {
              const Icon = ENTITY_ICONS[p.type] ?? NotebookPen;
              return (
                <span key={keyOf(p)} className="inline-flex items-center gap-1 rounded bg-bg-input px-1.5 py-0.5 text-xs text-text-secondary">
                  <Icon size={11} />
                  <span className="max-w-[12rem] truncate">{p.title}</span>
                  <button type="button" aria-label={`Remove ${p.title}`} onClick={() => toggle(p)} className="hover:text-danger"><X size={11} /></button>
                </span>
              );
            })}
          </div>
        )}
        <div className="px-3 py-2">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks, projects, people, tags..."
            aria-label="Search destinations"
            className="w-full rounded bg-bg-input px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <ul role="listbox" aria-label="Destinations" className="max-h-64 overflow-y-auto pb-1">
          {!query.trim() && options.length > 0 && <li className="px-3 py-1 text-[10px] uppercase tracking-wide text-text-muted">Recent</li>}
          {options.map((o, i) => {
            const Icon = ENTITY_ICONS[o.type] ?? NotebookPen;
            const on = pickedKeys.has(keyOf(o));
            return (
              <li
                key={keyOf(o)}
                role="option"
                aria-selected={on}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); toggle(o); }}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${i === active ? 'bg-bg-hover' : ''}`}
              >
                <Icon size={13} className="shrink-0 text-text-muted" />
                <span className="flex-1 truncate text-text-primary">{o.title}</span>
                <span className="text-[10px] text-text-muted">{TYPE_LABEL[o.type]}</span>
                {on && <Check size={13} className="text-accent" />}
              </li>
            );
          })}
          {query.trim() && options.length === 0 && <li className="px-3 py-2 text-xs text-text-muted">No matches</li>}
        </ul>
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border">
          <span className="text-[10px] text-text-muted">Enter picks, Ctrl+Enter sends</span>
          <button
            type="button"
            onClick={confirm}
            disabled={picked.length === 0 && initial.length === 0}
            className="rounded bg-accent px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
